import * as ng from 'angular';
import * as _ from 'underscore';

import { Experience, ExperienceTypeDef } from '../../';
import { EditorModel } from '../../editor/editor.model';
import { EditorPageModel } from '../../editor/editor-page.model';
import { GuestTypeService, GuestType } from '../../../guest-type';

import {RsvpPageType } from './rsvp-pages';
import {GatewayRadiusModel} from '../gateway-radius.model';

const settingsViewUrl = require('./rsvp-settings.html');
const headerViewUrl = require('./rsvp-page-header.html');
const footerViewUrl = require('./rsvp-page-footer.html');

ng.module('e360')
   .factory('rsvpEditorModelFactory', rsvpEditorModelFactory);

rsvpEditorModelFactory.$inject = ['instanceServiceFactory'];
function rsvpEditorModelFactory(instanceServiceFactory) {
   return instanceServiceFactory(RsvpEditorModel);
}

export interface RsvpEditorModelFactory {
   ({campaign, typeDef:ExperienceTypeDef, experience:Experience}):RsvpEditorModel
}

export class RsvpEditorModel extends EditorModel {
   readonly workspaceClass = 'rsvp';
   readonly settingsView = settingsViewUrl;
   private _pages:EditorPageModel[];

   gatewayRadiusMultiply:number = 0.001;
   readonly radiusOptions = [
      { label: 'Meters', value: 1 },
      { label: 'Kilometers', value: 0.001 },
      { label: 'Miles', value: 0.000621371 }
   ];

   readonly headerViewUrl = headerViewUrl;
   readonly footerViewUrl = footerViewUrl;
   readonly ageRequirementType:string;
   readonly orgSlug:string;

   domainInfo:{
      loading:boolean,
      zone?:{
         status:string,
         name_servers:string[]
      }
   }

   private gatewayRadiusModel:GatewayRadiusModel;

   guestTypes:GuestType[];
   showGuestInvitePage:boolean;
   showFriendInvitePage:boolean;

   static $inject = [
      'session',
      'l10n',
      'gettextCatalog',
      'showManageDomainModal',
      'showUrlGenerateModal',
      'experiencesService',
      'GuestTypeService',
   // separating instance args from injected services
   ].concat(['campaign', 'typeDef', 'experience']);

   constructor(
      public session,
      private l10n,
      private gettextCatalog,
      private showManageDomainModal,
      private showUrlGenerateModal,
      private experiencesService,
      guestTypeService:GuestTypeService,
      campaign, typeDef: ExperienceTypeDef, experience: Experience
   ) {
      super(campaign, typeDef, experience);

      this.hasSettings = true;
      this.hasPages = true;
      this.hasStaticPages = true;
      this.hasPreview = true;
      this.hasCode = false;
      this.orgSlug = this.session.getOrg().slug;

      // Adding this as a property for use on the view
      this.ageRequirementType = campaign.ageRequirementType;

      if (this.isNew) {
         const brandSettings = this.session.getBrand(campaign.brandId).settings.experience;

         const defaultSubdomain = campaign.name.toLowerCase().replace(/[^a-z0-9-]+/g, '');

         const defaultFields = [
            {
               label: { },
               description: { },
               key: 'firstName',
               row: 0,
               col: 0,
               sizeX: 1,
               sizeY: 3,
               type: 'text',
               required: true,
               minSizeY: 3
            },
            {
               label: { },
               description: { },
               key: 'lastName',
               row: 0,
               col: 1,
               sizeX: 1,
               sizeY: 3,
               type: 'text',
               required: true,
               minSizeY: 3
            },
            {
               label: { },
               description: { },
               key: 'email',
               row: 3,
               col: 0,
               sizeX: 1,
               sizeY: 3,
               type: 'text',
               required: true,
               minSizeY: 3
            },
            {
               label: { },
               description: { },
               key: 'dateOfBirth',
               row: 3,
               col: 1,
               sizeX: 1,
               sizeY: 3,
               type: 'text',
               required: true,
               minSizeY: 3
            },
            {
               label: { },
               description: { },
               key: 'country',
               required: true,
               row: 6,
               col: 0,
               sizeX: 1,
               sizeY: 3,
               type: 'text',
               minSizeY: 3
            },
            {
               label: { },
               description: { },
               key: 'zip',
               required: true,
               row: 6,
               col: 1,
               sizeX: 1,
               sizeY: 3,
               type: 'text',
               minSizeY: 3
            }
         ];

         const defaults = {
            mirrorCampaignFooter: true,
            showGuestType: false,
            guestsPerAttendee: 0,
            guestTypeGuestsPerAttendee: { },
            friendsPerAttendee: 0,
            subdomain: defaultSubdomain,
            footerText: {},
            customFields: defaultFields,
            userGateway: false,
            zipGateway: false,
            gatewayRadiusMeters: 120000,
            registeredCheck: 'active',
            backgroundColor: brandSettings.backgroundColor,
            buttonColor: brandSettings.buttonColor,
            buttonTextColor: brandSettings.buttonTextColor,
            font: brandSettings.mediaFont,
            fontSize: brandSettings.fontSize,
            fontColor: brandSettings.fontColor,
            footerBackgroundColor: brandSettings.footerBackgroundColor,
            footerFontSize: brandSettings.footerFontSize,
            footerFontColor: brandSettings.footerFontColor,
            footerHideDefaultLinks: brandSettings.footerHideDefaultLinks,
            footerLinkColor: brandSettings.footerLinkColor,
            footerLinkHoverColor: brandSettings.footerLinkHoverColor,
            socialLinkColor: brandSettings.socialLinkColor,
            socialLinkHoverColor: brandSettings.socialLinkHoverColor,
            titleFont: brandSettings.mediaFont,
            titleFontColor: brandSettings.headerFontColor,
            titleFontSize: brandSettings.headerFontSize,
            subTitleFontSize: brandSettings.fontSize,
            linkColor: brandSettings.linkColor,
            linkHoverColor: brandSettings.linkHoverColor,
            uploads: { headerImage: brandSettings.uploads.genericHeader },
            eventFilterLayout: 'none',
            eventSelectLayout: 'list',
            eventMapStyle: 1,
            termsURL: brandSettings.termsURL,
            privacyURL: brandSettings.privacyURL,
            cookiesURL: brandSettings.cookiesURL,
         };

         const optInFields = ['contactOptin', 'contestOptin', 'promoOptin', 'salesOptin', 'termsAgree'];

         // on the registration page, we add the opt-in fields that are pre-defined in the Brand Settings
         let _row = 9;
         for (let optIn of optInFields) {
            if (brandSettings[optIn] && !ng.equals({}, brandSettings[optIn])) {
               const field = {
                  label: { },
                  description: brandSettings[optIn],
                  key: optIn,
                   // if present, the terms agreement field is always required
                  required: optIn == 'termsAgree',
                  row: _row,
                  col: 0,
                  sizeX: 2,
                  sizeY: 3,
                  type: 'optin',
                  minSizeY: 1,
                  value: '0'
               }
               defaults.customFields.push(field)
               _row += 3;
            }
         }

         this.experience = {
            campaignId: campaign.id,
            type: typeDef.id,
            settings: ng.copy(defaults)
         };
      } else { // existing rsvp experience
         if (this.settings.domain) {
            this.loadDomainStatus();
         }
      }

      this.gatewayRadiusModel = new GatewayRadiusModel(this.settings.gatewayRadiusMeters, 1);

      this.setDefaultTextFields();

      guestTypeService.list(this.campaign.id)
         .then(list => {
            this.guestTypes = list;
            this.showGuestInvitePage = this.guestInvitePageHasGuests;
            this.showFriendInvitePage = this.friendInvitePageHasFriends;
         });

      this.createPages();

      // set default page index when loading the editor
      if (this.ageRequirementType || this.gateway == 'user') {
         this.defaultEditorPageIndex = 0
      } else {
         this.defaultEditorPageIndex = 1;
      }
   }

   get guestInvitePageHasGuests() {
      let _active = this.experience.settings.guestsPerAttendee > 0;
      if (!_active && this.guestTypes) {
         for (let gt of this.guestTypes) {
            if (this.experience.settings.guestTypeGuestsPerAttendee[gt.slug] > 0) {
               _active = true;
               break; 
            }
         }
      }
      return _active;
   }

   get friendInvitePageHasFriends() {
      return this.experience.settings.friendsPerAttendee > 0;
   }

   get pages() {
      return this._pages;
   }

   get gateway() {
      if (this.settings.zipGateway && this.settings.userGateway) {
         return 'user';
      } else if (this.settings.zipGateway) {
         return 'zip';
      } else if (this.settings.userGateway) {
         return 'user';
      }

      return 'none';
   }

   set gateway(gateway:string) {
      this.settings.zipGateway = false;
      this.settings.userGateway = false;

      switch (gateway) {
         case 'none':
            break;
         case 'zip':
            this.settings.zipGateway = true;
            this.settings.userGateway = false;
            break;
         case 'user':
            this.settings.userGateway = true;
            break;
      }
   }

   get gatewayRadius() {
      return parseFloat((this.settings.gatewayRadiusMeters * this.gatewayRadiusMultiply).toFixed(2));
   }

   set gatewayRadius(radius:number) {
      this.settings.gatewayRadiusMeters = Math.round(radius / this.gatewayRadiusMultiply);
   }

   get previewUrl() {
      return `//${this.domain}`;
   }

   get domain() {
      if (this.settings.domain) {
         return this.settings.domain;
      } else {
         return `${this.settings.subdomain}.${this.session.rsvpRootDomain()}`;
      }
   }

   get countryPlaceholder() {
      // per client request, B-F to display "Location" in place of "Country"
      return (this.orgSlug.indexOf('brown-forman') >= 0) ? 
         this.l10n.getTranslatedString('-- Select Location --', this.language) : 
         this.l10n.getTranslatedString('-- Select Country --', this.language);
   }

   createPages() {
      let viewPages = [];

      if (this.ageRequirementType) {
         viewPages.push(new RsvpPageModel(this, rsvpPageAgeGate, RsvpPageType.AgeGate));
      }

      viewPages.push(new RsvpPageModel(this, rsvpPageGeoLocationLookup, RsvpPageType.GeoLocationLookup));
      viewPages.push(new RsvpPageModel(this, rsvpPageZipCodeLookup, RsvpPageType.ZipCodeLookup));
      viewPages.push(new RsvpPageModel(this, rsvpPageEventSelect, RsvpPageType.EventSelect));
      viewPages.push(new RsvpPageModel(this, rsvpPageRegistration, RsvpPageType.Registration));
      viewPages.push(new RsvpPageModel(this, rsvpPageGuestInvite, RsvpPageType.GuestInvite));
      viewPages.push(new RsvpPageModel(this, rsvpPageFriendInvite, RsvpPageType.FriendInvite));
      viewPages.push(new RsvpPageModel(this, rsvpPageConfirmation, RsvpPageType.Confirmation));
      this._pages = viewPages;
   }

   setDefaultTextFields() {
      const translations = [
        { key: 'pageTitle', original: 'RSVP Site' },
        { key: 'ageGateTitle', original: 'Confirm Age' },
        { key: 'geoLocationLookupTitle', original: 'Checking location' },
        { key: 'geoLocationLookupText', original: 'We\'re checking your location to provide events in your area. If asked for permission please click "Allow" when prompted.' },
        { key: 'zipCodeLookupTitle', original: 'Welcome' },
        { key: 'zipCodeLookupText', original: 'Please enter your ZIP/Postal code below to find the location of an event nearest you.' },
        { key: 'eventsTitle', original: 'Select Event' },
        { key: 'eventsText', original: 'Below are the events in your area. Please click on a date and time that is convenient for you.' },
        { key: 'registerTitle', original: 'Register' },
        { key: 'registerText', original: 'Please provide the following information.' },
        { key: 'guestInviteTitle', original: 'Invite Guests' },
        { key: 'guestInviteExtraText', original: 'Please invite guests by adding their details below.' },
        { key: 'friendInviteTitle', original: 'Tell your friends' },
        { key: 'friendInviteText', original: 'Tell your friends about this event.  We\'ll email them letting them know the details of this event and how to register.' },
        { key: 'confirmationTitle', original: 'Confirmation' },
        { key: 'confirmationText', original: 'You are registered. We look forward to seeing you there.<br/><br/>Please arrive promptly. Space is limited.' },
      ];

      // per client request, B-F to display "Location" in place of "Country"
      if (this.orgSlug.indexOf('brown-forman') >= 0) {
         translations.push({ key: 'countryTitle', original: 'Location' });
      } else {
         translations.push({ key: 'countryTitle', original: 'Country' });
      }

      for (const { key, original } of translations) {
         this.l10n.translateObject(this.experience.settings, key, original, this.languages);
      }
   }

   private loadDomainStatus() {
      this.domainInfo = { loading:true };
      this.experiencesService.cloudFlareVerify(this.experience.id).then(res => {
         if (res.result.length > 0) {
            this.domainInfo.zone = res.result[0];
         }
         this.domainInfo.loading = false;
      });
   }

   checkNameservers() {
      this.manageDomain('nameservers');
   }

   manageDomain(stage:string = 'config') {
      let nameservers:string[];
      if (this.domainInfo) {
         nameservers = this.domainInfo.zone.name_servers;
      }

      this.showManageDomainModal(stage, this, nameservers).then(result => {
         this.settings.domain = result.domain;
         this.settings.subdomain = result.subdomain;
      });
   }

   showUrlBuilder() {
      this.showUrlGenerateModal(this.settings.allowGuestTypeInURL, this.domain, false);
   }
}

/* ================ RSVP Page Model ================ */
const rsvpPageRegistration = require('./rsvp-page-registration.html');
const rsvpPageAgeGate = require('./rsvp-page-age-gate.html');
const rsvpPageEventSelect = require('./rsvp-page-event-select.html');
const rsvpPageConfirmation = require('./rsvp-page-confirmation.html');
const rsvpPageGuestInvite = require('./rsvp-page-guest-invite.html');
const rsvpPageFriendInvite = require('./rsvp-page-friend-invite.html');
const rsvpPageGeoLocationLookup = require('./rsvp-page-geolocation-lookup.html');
const rsvpPageZipCodeLookup = require('./rsvp-page-zip-lookup.html');

export class RsvpPageModel extends EditorPageModel {
   readonly view:string;
   type:RsvpPageType;

   constructor(editor:RsvpEditorModel, view:string, pageType:RsvpPageType) {
      super(editor);
      this.view = view;
      this.type = pageType;
   }

   get sortable() {
      return false; // RSVP flow is fixed, and cannot be sorted
   }

   get settings() {
      let index = this.editor.pages.indexOf(this);
      return this.editor.settings.pages[index];
   }
}
